import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Check, ChevronDown, Plus, Shield } from "lucide-react";
import type { CurrentUserOrganization, PendingInvitation } from "@multi-chat/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import {
  AlertDialog,
  AlertDialogCloseButton,
  AlertDialogDescription,
  AlertDialogMotionContent,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import {
  RAIL_CARD,
  RAIL_FOCUS,
  RAIL_IDENTITY_ICON_WELL,
} from "../../../components/layout/rail-compact.js";
import { useBeginWorkspaceSwitch } from "../../../components/layout/workspace-switch-loader.js";
import { Button } from "../../../components/ui/button/index.js";
import { FeedBotLogo } from "../../../components/brand/FeedBotLogo.js";
import { isApiError } from "../../../api/client.js";
import { acceptMyInvitation } from "../../../api/team-api.js";
import { AnimatePresence, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { useAuth } from "../../auth/auth-context.js";
import { authEntryCreatePanelMotion } from "../../auth/entry/auth-entry-motion.js";
import {
  AUTH_ENTRY_PAIRED_ACTION,
  AUTH_ENTRY_SECONDARY_ACTION,
} from "../../auth/entry/auth-entry-styles.js";
import { CreateOrganizationForm } from "../CreateOrganizationForm.js";
import { useOrganizations } from "../organization-context.js";
import {
  OrganizationIcon,
  organizationAccessKind,
  organizationIconForRole,
  ORGANIZATION_ICON_TONE,
  SUPER_ADMIN_ICON_TONE,
} from "../OrganizationIcon.js";
import { PlatformAdminOrganizationDirectoryDialog } from "../PlatformAdminOrganizationDirectory.js";
import { groupOrganizations, realMembershipRoleOf } from "../organization-selection.js";
import {
  applySwitcherChoice,
  organizationSwitcherDestinations,
  organizationSwitcherMenuVisible,
  switcherChoiceChangesWorkspace,
  WORKSPACE_HOME_PATH,
  type SwitcherChoice,
} from "../organization-switcher-navigation.js";
import { markWorkspaceEntered } from "../workspace-entry.js";
import { isSuperAdminPath } from "../../super-admin/super-admin-path.js";
import { useSuperAdminAccess } from "../../super-admin/use-super-admin-access.js";
import { SwitcherScrollArea } from "./SwitcherScrollArea.js";

// The keyboard ring is inset because the list's scroll area clips anything
// drawn outside a row, and the scroll margin keeps a focused row above the
// "more below" hint. Radix focuses rows on hover, and Chrome can report that
// focus as :focus-visible, so the ring is dropped while the pointer drives the menu.
const MENU_ITEM =
  "group min-h-12 min-w-0 scroll-mb-9 items-center gap-3 rounded-[10px] px-2.5 py-2 transition-colors duration-150 data-[highlighted]:bg-[#f3f8f6] focus-visible:shadow-[inset_0_0_0_2px_#177e678c] in-data-[input=pointer]:focus-visible:shadow-none";
const MENU_ITEM_ACTIVE =
  "bg-[#ecf7f2] shadow-[inset_0_0_0_1px_#cfe8dd] data-[highlighted]:bg-[#e4f3ec] in-data-[input=pointer]:focus-visible:shadow-[inset_0_0_0_1px_#cfe8dd]";
const MENU_LABEL = "m-0 px-2.5 pt-2.5 pb-1.5 text-[11px] leading-4 font-[700] text-[#7c958b]";
const MENU_ICON_BOX = "size-8 rounded-[9px]";
const MENU_ICON_WELL = cn(MENU_ICON_BOX, ORGANIZATION_ICON_TONE.member);
const MENU_ICON_SIZE = 16;
const MENU_SEPARATOR = "mx-1.5 my-1.5 bg-[#e8efeb]";
const RAIL_ICON_SIZE = 19;

function railCardClassName(compact: boolean): string {
  return cn(
    RAIL_CARD,
    "flex w-full min-w-0 items-center rounded-[12px] text-start text-[#effbf7]",
    compact ? "justify-center p-2" : "gap-[11px] p-[10px]",
  );
}

interface OrganizationSwitcherProps {
  compact?: boolean;
  onSelect?: () => void;
  /**
   * Super Admin shell already proved PlatformAdmin access.
   * The customer shell omits this and uses the PlatformAdmin probe.
   */
  platformAdmin?: boolean;
}

function OrganizationNameRole({
  name,
  role,
  statusLabel = null,
  onRail = false,
}: {
  name: string;
  role: string | null;
  statusLabel?: string | null;
  onRail?: boolean;
}): React.JSX.Element {
  return (
    <span className="min-w-0 flex-1 text-start">
      <span
        className={cn(
          "block truncate text-start",
          onRail
            ? "text-[12px] leading-[1.5] font-[750]"
            : "text-[13.5px] leading-5 font-[700] text-[#17302e]",
        )}
        title={name}
      >
        <bdi>{name}</bdi>
      </span>
      {role ? (
        <span
          className={cn(
            "block truncate text-start",
            onRail ? "text-[10px] leading-[1.5] text-[#8eada5]" : "text-[11.5px] leading-4 text-[#7c958b]",
          )}
        >
          {role}
        </span>
      ) : null}
      {statusLabel ? (
        <span
          className={cn(
            "block truncate text-start font-medium text-warning",
            onRail ? "text-[10px] leading-[1.5]" : "text-[0.6875rem] leading-4",
          )}
        >
          {statusLabel}
        </span>
      ) : null}
    </span>
  );
}

function SuperAdminMark({ onRail = false }: { onRail?: boolean }): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-grid size-7 shrink-0 place-items-center rounded-md border",
        onRail ? RAIL_IDENTITY_ICON_WELL : cn(MENU_ICON_BOX, SUPER_ADMIN_ICON_TONE),
      )}
      data-testid="organization-switcher-super-admin-icon"
    >
      <Shield size={onRail ? RAIL_ICON_SIZE : MENU_ICON_SIZE} strokeWidth={1.8} aria-hidden="true" />
    </span>
  );
}

function SelectedCheck({ selected }: { selected: boolean }): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-grid size-5 shrink-0 place-items-center rounded-full transition-opacity duration-150",
        selected ? "bg-brand-primary text-white opacity-100" : "opacity-0",
      )}
    >
      <Check size={12} strokeWidth={3} aria-hidden="true" />
    </span>
  );
}

export function OrganizationSwitcher({
  compact = false,
  onSelect,
  platformAdmin,
}: OrganizationSwitcherProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { phase, refreshAfterAuth } = useAuth();
  const {
    organizations,
    selected,
    select,
    selectCreatedOrganization,
    platformAdmin: platformAdminAccess,
  } = useOrganizations();
  const beginWorkspaceSwitch = useBeginWorkspaceSwitch();
  const detectedPlatformAdmin = useSuperAdminAccess();
  const isPlatformAdmin = platformAdmin ?? (platformAdminAccess || detectedPlatformAdmin);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const onSuperAdminPath = isSuperAdminPath(location.pathname);
  const pendingInvitations = phase.kind === "authenticated" ? phase.user.pendingInvitations : [];
  const [createOpen, setCreateOpen] = useState(false);
  const reduceMotion = useReducedMotion() ?? false;
  const createPanelMotion = authEntryCreatePanelMotion(reduceMotion);
  const [joinError, setJoinError] = useState<string | null>(null);
  const superAdminLabel = t("superAdmin.badge");
  const suspendedLabel =
    !onSuperAdminPath && selected?.status === "SUSPENDED"
      ? t("organization.suspended.badge")
      : null;
  const selectedName = onSuperAdminPath
    ? superAdminLabel
    : (selected?.name ?? t("organization.switcher.none"));
  const ownsOrganization = organizations.some(
    (organization) => organization.access !== "PLATFORM_ADMIN" && organization.role === "OWNER",
  );
  // Platform Admin access hides real ownership, so creation is not offered there.
  const canCreateOrganization = !ownsOrganization && !platformAdminAccess;
  const showMenu = organizationSwitcherMenuVisible({
    organizationCount: organizations.length,
    canCreateOrganization,
    pendingInvitationCount: pendingInvitations.length,
    platformAdmin: isPlatformAdmin,
  });
  const grouped = groupOrganizations(organizations);
  const hasOwned = grouped.owned.length > 0;
  const hasList =
    hasOwned ||
    grouped.members.length > 0 ||
    grouped.systemManaged.length > 0 ||
    platformAdminAccess ||
    pendingInvitations.length > 0;
  const showSuperAdminDestination = organizationSwitcherDestinations({
    organizationIds: organizations.map((organization) => organization.id),
    platformAdmin: isPlatformAdmin,
  }).some((destination) => destination.kind === "super-admin");

  function openWorkspace(path: string): void {
    markWorkspaceEntered();
    navigate(path);
  }

  async function joinInvitation(invitation: PendingInvitation): Promise<void> {
    setJoinError(null);
    try {
      const accepted = await acceptMyInvitation(invitation.invitationId);
      selectCreatedOrganization(accepted.organizationId);
      markWorkspaceEntered();
      refreshAfterAuth();
      if (isSuperAdminPath(location.pathname)) navigate(WORKSPACE_HOME_PATH);
      onSelect?.();
    } catch (caught) {
      setJoinError(isApiError(caught) ? caught.message : t("team.invite.acceptError"));
    }
  }

  function beginSwitchIfNeeded(choice: SwitcherChoice): void {
    if (
      switcherChoiceChangesWorkspace({
        choice,
        currentPath: location.pathname,
        selectedOrganizationId: selected?.id ?? null,
      })
    ) {
      beginWorkspaceSwitch();
    }
  }

  function choose(id: number): void {
    const choice: SwitcherChoice = { kind: "organization", organizationId: id };
    beginSwitchIfNeeded(choice);
    const next = applySwitcherChoice({ choice, currentPath: location.pathname });
    if (next.persistOrganizationId !== null) select(next.persistOrganizationId);
    if (next.navigateTo) openWorkspace(next.navigateTo);
    onSelect?.();
  }

  function accessLabel(
    organization: Pick<CurrentUserOrganization, "role" | "access" | "membershipRole">,
  ): string {
    const role = realMembershipRoleOf(organization);
    return role === null
      ? t("organization.platformAdmin.systemManagedAccess")
      : t(`organization.role.${role.toLowerCase()}`);
  }

  function chooseSuperAdmin(): void {
    const choice: SwitcherChoice = { kind: "super-admin" };
    beginSwitchIfNeeded(choice);
    const next = applySwitcherChoice({ choice, currentPath: location.pathname });
    if (next.navigateTo) navigate(next.navigateTo);
    onSelect?.();
  }

  function renderOrganizationItem(
    organization: (typeof organizations)[number],
    subtitle: string | null,
  ): React.JSX.Element {
    const active = !onSuperAdminPath && organization.id === selected?.id;
    const role = realMembershipRoleOf(organization);
    return (
      <DropdownMenuItem
        key={organization.id}
        className={cn(MENU_ITEM, active && MENU_ITEM_ACTIVE)}
        data-testid={`organization-option-${organization.id}`}
        onSelect={() => choose(organization.id)}
      >
        <OrganizationIcon
          size="sm"
          className={cn(MENU_ICON_BOX, ORGANIZATION_ICON_TONE[organizationAccessKind(role)])}
          icon={organizationIconForRole(role)}
          iconSize={MENU_ICON_SIZE}
          iconClassName="[stroke-width:1.8]"
        />
        <OrganizationNameRole
          name={organization.name}
          role={subtitle}
          statusLabel={
            organization.status === "SUSPENDED" ? t("organization.suspended.badge") : null
          }
        />
        <SelectedCheck selected={active} />
      </DropdownMenuItem>
    );
  }

  function closeCreate(): void {
    setCreateOpen(false);
  }

  const identity = onSuperAdminPath ? (
    <SuperAdminMark onRail />
  ) : (
    <OrganizationIcon
      size="sm"
      className={RAIL_IDENTITY_ICON_WELL}
      icon={selected ? organizationIconForRole(realMembershipRoleOf(selected)) : Building2}
      iconSize={RAIL_ICON_SIZE}
      iconClassName="[stroke-width:1.8]"
    />
  );

  if (!showMenu) {
    if (compact) {
      return (
        <SimpleTooltip content={selectedName}>
          <div
            className={railCardClassName(true)}
            data-testid="organization-switcher"
            aria-label={selectedName}
          >
            {identity}
          </div>
        </SimpleTooltip>
      );
    }

    return (
      <div
        className={railCardClassName(false)}
        data-testid="organization-switcher"
        aria-label={selectedName}
      >
        {identity}
        <OrganizationNameRole
          name={selected?.name ?? selectedName}
          role={selected ? accessLabel(selected) : null}
          statusLabel={suspendedLabel}
          onRail
        />
      </div>
    );
  }

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        className={cn(
          "group transition-[background-color,border-color] duration-150 hover:border-[rgba(213,255,239,0.16)] hover:bg-white/[0.08] data-[state=open]:bg-white/[0.08]",
          railCardClassName(compact),
          RAIL_FOCUS,
        )}
        aria-label={t("organization.switcher.open", { name: selectedName })}
        data-testid="organization-switcher"
      >
        {identity}
        {compact ? null : (
          <>
            <OrganizationNameRole
              name={selectedName}
              role={
                onSuperAdminPath
                  ? t("organization.switcher.platform")
                  : selected
                    ? accessLabel(selected)
                    : null
              }
              statusLabel={suspendedLabel}
              onRail
            />
            <ChevronDown
              size={16}
              strokeWidth={1.8}
              className="shrink-0 text-[#91aea5] transition-transform duration-150 group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
          </>
        )}
      </button>
    </DropdownMenuTrigger>
  );

  return (
    <>
      <DropdownMenu>
        {compact ? <SimpleTooltip content={selectedName}>{trigger}</SimpleTooltip> : trigger}
        <DropdownMenuContent
          side={compact ? (i18n.dir() === "rtl" ? "left" : "right") : "bottom"}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          aria-label={t("organization.switcher.menuTitle")}
          onPointerMove={(event) => {
            event.currentTarget.dataset.input = "pointer";
          }}
          onKeyDown={(event) => {
            event.currentTarget.dataset.input = "keyboard";
          }}
          className="font-feedbot flex max-h-[min(var(--radix-dropdown-menu-content-available-height),34rem)] w-[min(18rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-[16px] border-[#dce7df] bg-white p-1.5 text-[#17302e] shadow-[0_24px_60px_#06251d2e]"
        >
          {hasList ? (
            <SwitcherScrollArea label={t("organization.switcher.listLabel")}>
              {hasOwned ? (
                <>
                  <p className={MENU_LABEL} data-testid="organization-switcher-owned">
                    {grouped.owned.length === 1
                      ? t("organization.switcher.ownedOrganization")
                      : t("organization.switcher.ownedOrganizations")}
                  </p>
                  {grouped.owned.map((organization) => renderOrganizationItem(organization, accessLabel(organization)))}
                  {grouped.members.length > 0 ? <DropdownMenuSeparator className={MENU_SEPARATOR} /> : null}
                </>
              ) : null}
              {grouped.members.length > 0 ? (
                <>
                  <p className={MENU_LABEL} data-testid="organization-switcher-members">
                    {t("organization.switcher.memberOrganizations")}
                  </p>
                  {grouped.members.map((organization) => renderOrganizationItem(organization, accessLabel(organization)))}
                </>
              ) : null}
              {grouped.systemManaged.length > 0 || platformAdminAccess ? (
                <>
                  {hasOwned || grouped.members.length > 0 ? (
                    <DropdownMenuSeparator className={MENU_SEPARATOR} />
                  ) : null}
                  <p className={MENU_LABEL} data-testid="organization-switcher-platform-access">
                    {t("organization.platformAdmin.managementGroup")}
                  </p>
                  {grouped.systemManaged.map((organization) => renderOrganizationItem(organization, null))}
                  {platformAdminAccess ? (
                    <DropdownMenuItem
                      className={MENU_ITEM}
                      data-testid="organization-switcher-all-organizations"
                      onSelect={() => setDirectoryOpen(true)}
                    >
                      <span className={cn("inline-grid shrink-0 place-items-center border", MENU_ICON_WELL)}>
                        <Building2 size={MENU_ICON_SIZE} strokeWidth={1.8} aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-start text-[13.5px] font-[700] text-[#17302e]">
                        {t("organization.platformAdmin.browseAll")}
                      </span>
                    </DropdownMenuItem>
                  ) : null}
                </>
              ) : null}
              {pendingInvitations.length > 0 ? (
                <>
                  {hasOwned || grouped.members.length > 0 || grouped.systemManaged.length > 0 || platformAdminAccess ? (
                    <DropdownMenuSeparator className={MENU_SEPARATOR} />
                  ) : null}
                  <p className={MENU_LABEL} data-testid="organization-switcher-invitations">
                    {t("organization.switcher.pendingInvitations")}
                  </p>
                  {pendingInvitations.map((invitation) => (
                    <DropdownMenuItem
                      key={invitation.invitationId}
                      className={MENU_ITEM}
                      data-testid={`organization-switcher-join-${invitation.invitationId}`}
                      onSelect={() => void joinInvitation(invitation)}
                    >
                      <OrganizationIcon
                        size="sm"
                        className={MENU_ICON_WELL}
                        iconSize={MENU_ICON_SIZE}
                        iconClassName="[stroke-width:1.8]"
                      />
                      <OrganizationNameRole
                        name={invitation.organizationName}
                        role={t(`organization.role.${invitation.role.toLowerCase()}`)}
                      />
                      <span className="shrink-0 rounded-full bg-brand-mint px-2 py-0.5 text-[11px] font-[750] text-brand-primary">
                        {t("team.invite.accept")}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </>
              ) : null}
            </SwitcherScrollArea>
          ) : null}
          {showSuperAdminDestination || canCreateOrganization ? (
            <div className="shrink-0">
              {showSuperAdminDestination ? (
                <>
                  {hasList ? <DropdownMenuSeparator className={MENU_SEPARATOR} /> : null}
                  <DropdownMenuItem
                    className={cn(MENU_ITEM, onSuperAdminPath && MENU_ITEM_ACTIVE)}
                    data-testid="organization-switcher-super-admin"
                    onSelect={chooseSuperAdmin}
                  >
                    <SuperAdminMark />
                    <OrganizationNameRole name={superAdminLabel} role={t("organization.switcher.platform")} />
                    <SelectedCheck selected={onSuperAdminPath} />
                  </DropdownMenuItem>
                </>
              ) : null}
              {canCreateOrganization ? (
                <>
                  <DropdownMenuSeparator className={MENU_SEPARATOR} />
                  <DropdownMenuItem
                    data-testid="organization-switcher-create"
                    className={cn(MENU_ITEM, "font-[700] text-brand-primary")}
                    onSelect={() => setCreateOpen(true)}
                  >
                    <span className="inline-grid size-8 shrink-0 place-items-center rounded-[9px] border border-dashed border-[#a9d6c5] text-brand-primary transition-colors duration-150 group-data-[highlighted]:border-brand-primary group-data-[highlighted]:bg-brand-mint">
                      <Plus size={MENU_ICON_SIZE} strokeWidth={2} aria-hidden="true" data-testid="organization-switcher-create-icon" />
                    </span>
                    <span className="text-[13.5px]">{t("organization.switcher.create")}</span>
                  </DropdownMenuItem>
                </>
              ) : null}
            </div>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {joinError ? (
        <p className="m-0 px-2 pt-1 text-[0.75rem] text-destructive" role="alert">
          {joinError}
        </p>
      ) : null}

      {platformAdminAccess ? (
        <PlatformAdminOrganizationDirectoryDialog
          open={directoryOpen}
          onOpenChange={setDirectoryOpen}
          selectedOrganizationId={onSuperAdminPath ? null : (selected?.id ?? null)}
          onEnter={(organizationId) => {
            setDirectoryOpen(false);
            choose(organizationId);
          }}
        />
      ) : null}

      <AlertDialog open={createOpen} onOpenChange={setCreateOpen}>
        <AnimatePresence>
          {createOpen ? (
            <AlertDialogMotionContent
              key="create-organization"
              panelVariants={createPanelMotion}
              className="font-feedbot w-[min(calc(100vw-2rem),28rem)] gap-6 rounded-[18px] border-[#dce7df] bg-white p-5 text-[#17302e] shadow-[0_30px_100px_#06251d35] sm:p-7"
            >
              <AlertDialogCloseButton
                label={t("common.close")}
                onClose={closeCreate}
                className="rounded-[8px] text-[#82958b] hover:bg-[#f3f7f6] hover:text-[#17302e] focus-visible:ring-brand/35"
              />
              <FeedBotLogo
                tone="onLight"
                animated
                label={t("auth.wordmarkLabel")}
                className="w-[128px] justify-self-start"
              />
              <div className="grid gap-2 text-start">
                <AlertDialogTitle className="text-[22px] leading-[1.2] font-[700] tracking-[-0.04em] text-[#12372e]">
                  {t("organization.select.createTitle")}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm leading-normal text-[#82958b]">
                  {t("organization.create.subtitle")}
                </AlertDialogDescription>
              </div>
              <CreateOrganizationForm
                submitLabel={t("organization.create.submit")}
                onCreated={(organizationId) => {
                  setCreateOpen(false);
                  selectCreatedOrganization(organizationId);
                  markWorkspaceEntered();
                  refreshAfterAuth();
                  if (isSuperAdminPath(location.pathname)) navigate(WORKSPACE_HOME_PATH);
                  onSelect?.();
                }}
                secondaryAction={
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(AUTH_ENTRY_SECONDARY_ACTION, AUTH_ENTRY_PAIRED_ACTION)}
                    data-testid="organization-switcher-create-cancel"
                    onClick={closeCreate}
                  >
                    {t("common.cancel")}
                  </Button>
                }
              />
            </AlertDialogMotionContent>
          ) : null}
        </AnimatePresence>
      </AlertDialog>
    </>
  );
}
